/**
 * Loans to employees — types and calls. Live-only admin writes, like the
 * roster: not queued in the offline outbox. The repaying is done by the pay
 * run on the Worker; this side only gives, re-paces, approves and voids.
 */

import { request, uploadBlob, uuidv7 } from './api';

export interface LoanRow {
  id: string;
  worker_id: string;
  worker_name: string;
  given_on: string;
  amount_rs: number;
  weekly_rs: number;
  reason: string;
  status: 'unapproved' | 'approved';
  repaid_rs: number;
  balance_rs: number;
  created_at: string;
  created_by: string;
  created_by_name: string;
  approved_by: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  voided_at: string | null;
  void_reason: string | null;
  /** 1 once he has signed the receipt on the tablet. */
  signed: number;
  signed_at: string | null;
  /** When the cash was handed over and he signed — only then does it repay. */
  issued_at: string | null;
  issued_by_name: string | null;
  /** A member is paid monthly, so his loan is repaid once a month (migration 0014). */
  repay_cycle: 'weekly' | 'monthly';
  row_version: number;
}

export interface LoanRepayment {
  pay_run_id: string;
  amount_rs: number;
  balance_after_rs: number;
  period_from: string;
  period_to: string;
  status: 'unapproved' | 'approved';
}

/** Where a loan stands, in the words the list shows. */
export type LoanState = 'voided' | 'waiting' | 'toIssue' | 'repaying' | 'paid';

/**
 * Requested → approved → issued (migration 0013). "toIssue": the owner has
 * approved it and the cash has not been handed over and signed for yet.
 */
export function loanState(loan: LoanRow): LoanState {
  if (loan.voided_at) return 'voided';
  if (loan.status === 'unapproved') return 'waiting';
  if (!loan.issued_at) return 'toIssue';
  return loan.balance_rs === 0 ? 'paid' : 'repaying';
}

/** "a week" or "a month" — how often this loan's repayment comes off. */
export function perPeriod(cycle: 'weekly' | 'monthly'): string {
  return cycle === 'monthly' ? 'a month' : 'a week';
}

/** "weeks" or "months". */
export function periods(cycle: 'weekly' | 'monthly', n: number): string {
  return cycle === 'monthly' ? (n === 1 ? 'month' : 'months') : n === 1 ? 'week' : 'weeks';
}

/** How many more pay runs at this pace, if every week has the pay for it. */
export function weeksToRepay(balanceRs: number, weeklyRs: number): number {
  return weeklyRs > 0 ? Math.ceil(balanceRs / weeklyRs) : 0;
}

export const loanApi = {
  list: () => request<{ loans: LoanRow[] }>('/loans'),

  get: (id: string) => request<{ loan: LoanRow; repayments: LoanRepayment[] }>(`/loans/${id}`),

  /** The clerk's is a request for the owner's Approvals; the owner's is approved at once. */
  give: (body: { workerId: string; amountRs: number; weeklyRs: number; reason: string }) =>
    request<{ loan: LoanRow }>('/loans', { method: 'POST', body: { id: uuidv7(), ...body } }),

  /** The owner, at any time; the clerk only while it waits for approval. */
  update: (
    id: string,
    rowVersion: number,
    patch: { weeklyRs?: number; amountRs?: number; reason?: string },
  ) => request<{ loan: LoanRow }>(`/loans/${id}`, { method: 'PATCH', body: { rowVersion, ...patch } }),

  approve: (id: string) => request<{ loan: LoanRow }>(`/loans/${id}/approve`, { method: 'POST' }),

  /** Handing over the cash: his signature, a PNG straight to R2. The first one issues the loan. */
  saveSignature: (id: string, png: Blob) =>
    uploadBlob<{ loan: LoanRow }>(`/loans/${id}/signature`, png, 'image/png'),

  /** A path for SignatureImage, which fetches it with the session token. */
  // ?v= changes with each signing, so a re-signed receipt is not served from
  // the browser's 10-minute image cache. The Worker ignores it.
  signaturePath: (id: string, signedAt?: string | null) =>
    `/loans/${id}/signature${signedAt ? `?v=${encodeURIComponent(signedAt)}` : ''}`,

  void: (id: string, reason: string) =>
    request<{ loan: LoanRow }>(`/loans/${id}/void`, { method: 'POST', body: { reason } }),
};
